import { useEffect, useState, useCallback } from 'react';
import { getCustomers } from 'api/customer';
import { getAddresses, searchAddressTypes, createAddress, updateAddress } from 'api/address';
import { makeCustomersFormat } from 'containers/pages/customers/libs';
import { makeAddressesFormat, filterAddresses, parseAddressTypesResult } from 'containers/pages/addressManagement/libs';
import { useNotificationProvider } from 'hooks';
import { Customer } from 'containers/pages/customers/types';
import { CustomerOption, Address, AddressType } from 'containers/pages/addressManagement/types';

function useAddressManagement() {
  const { notifySuccess, notifyError } = useNotificationProvider();

  const [isLoading, setIsLoading] = useState(false);
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [searchedAddresses, setSearchedAddresses] = useState<Address[]>([]);
  const [addressTypes, setAddressTypes] = useState<AddressType[]>([]);
  const [searchInput, setSearchInput] = useState('');

  // Toggle add form open/close
  const onToggleForm = () => {
    setIsOpenForm((prev) => !prev);
  };

  // Customer selection handler
  const onCustomerChange = useCallback((_event: unknown, newValue: CustomerOption | null) => {
    setSelectedCustomer(newValue);
    setIsOpenForm(false);
    setSearchInput('');
    if (!newValue) {
      setAddresses([]);
      setSearchedAddresses([]);
    }
  }, []);

  // Search handlers
  const onSearchInput = (input: string) => {
    setSearchInput(input);
    if (!input) {
      setSearchedAddresses(addresses);
    } else {
      const filtered = filterAddresses(input, addresses);
      setSearchedAddresses(filtered);
    }
  };

  const onResetSearch = () => {
    setSearchInput('');
    setSearchedAddresses(addresses);
  };

  // Save handler (create or update)
  const onSaveAddress = async (formData: Record<string, unknown>, isEdit: boolean) => {
    try {
      if (isEdit) {
        await updateAddress(formData);
        notifySuccess('Address updated successfully');
      } else {
        await createAddress(formData);
        notifySuccess('Address created successfully');
        if (selectedCustomer) {
          await onRefreshAddresses(selectedCustomer.Id);
        }
        setIsOpenForm(false);
      }
    } catch (error) {
      console.error('[useAddressManagement] Save failed:', error);
      notifyError('Failed to save address. Please try again.');
    }
  };

  // Refresh address list
  const onRefreshAddresses = useCallback(async (customerId: number) => {
    try {
      const result = await getAddresses({ CustomerId: customerId });
      const formatted = makeAddressesFormat(result);
      setAddresses(formatted);
      setSearchedAddresses(formatted);
      setSearchInput('');
    } catch (error) {
      console.error('[useAddressManagement] Refresh failed:', error);
      notifyError('Failed to refresh addresses');
    }
  }, [notifyError]);

  // Load addresses when customer changes
  useEffect(() => {
    if (selectedCustomer) {
      const loadAddresses = async () => {
        setIsLoading(true);
        try {
          const result = await getAddresses({ CustomerId: selectedCustomer.Id });
          const formatted = makeAddressesFormat(result);
          setAddresses(formatted);
          setSearchedAddresses(formatted);
        } catch (error) {
          console.error('[useAddressManagement] Load addresses failed:', error);
          notifyError('Failed to load addresses');
        } finally {
          setIsLoading(false);
        }
      };
      loadAddresses();
    }
  }, [selectedCustomer, notifyError]);

  // Initial data load (customers + address types)
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [customersSettled, typesSettled] = await Promise.allSettled([
          getCustomers({ SearchKeyword: '', Page: 1, Take: 1000 }),
          searchAddressTypes({ SearchKeyword: '', Take: 100 }),
        ]);

        if (customersSettled.status === 'fulfilled') {
          const formatted = makeCustomersFormat(customersSettled.value) as Customer[];
          const options: CustomerOption[] = formatted.map((c) => ({ Id: c.Id, Name: c.Name }));
          setCustomerOptions(options);
        } else {
          console.error('[useAddressManagement] Customers load failed:', customersSettled.reason);
          notifyError('Failed to load customers');
        }

        if (typesSettled.status === 'fulfilled') {
          setAddressTypes(parseAddressTypesResult(typesSettled.value));
        } else {
          console.error('[useAddressManagement] Address types load failed:', typesSettled.reason);
        }
      } catch (error) {
        console.error('[useAddressManagement] Initial load failed:', error);
        notifyError('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [notifyError]);

  const state = {
    isLoading,
    isOpenForm,
    customerOptions,
    selectedCustomer,
    searchedAddresses,
    addressTypes,
    searchInput,
  };

  const handler = {
    onToggleForm,
    onCustomerChange,
    onSearchInput,
    onResetSearch,
    onSaveAddress,
    onRefreshAddresses,
  };

  return { state, handler };
}

export default useAddressManagement;
